
import React, { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Link } from 'react-router-dom';
import { UserRole } from '@/types';
import { useLanguage } from '@/context/language-context';
import { RoleSelector } from './RoleSelector';

const seedphraseRegisterSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  role: z.enum(['client', 'escort']).default('client'),
  terms: z.boolean().refine(val => val === true, {
    message: "You must agree to the terms and conditions",
  }),
});

export type SeedphraseRegisterFormValues = z.infer<typeof seedphraseRegisterSchema>;

interface SeedphraseRegisterFormProps {
  onSubmit: (data: SeedphraseRegisterFormValues) => Promise<void>;
  isLoading: boolean;
}

export const SeedphraseRegisterForm: React.FC<SeedphraseRegisterFormProps> = ({ 
  onSubmit,
  isLoading 
}) => {
  const { t } = useLanguage();

  const form = useForm<SeedphraseRegisterFormValues>({
    resolver: zodResolver(seedphraseRegisterSchema),
    defaultValues: {
      name: '',
      role: 'client',
      terms: false,
    },
  });

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-1">
        <Label htmlFor="seedphrase-name">{t('Name')}</Label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            id="seedphrase-name"
            placeholder={t('Your name')}
            className="pl-10"
            {...form.register('name')}
          />
        </div>
        {form.formState.errors.name && (
          <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
        )}
      </div>
      
      <RoleSelector 
        selectedRole={form.watch('role') as UserRole}
        idPrefix="seedphrase-"
        onRoleChange={(value) => form.setValue('role', value as 'client' | 'escort')}
      />
      
      <Alert variant="default" className="bg-muted/50">
        <AlertDescription>
          {t('Create a seedphrase for anonymous login')}
        </AlertDescription>
      </Alert>
      
      <div className="flex items-center space-x-2">
        <Checkbox 
          id="seedphrase-terms" 
          checked={form.watch('terms')}
          onCheckedChange={(checked) => {
            form.setValue('terms', checked === true);
          }}
        />
        <Label 
          htmlFor="seedphrase-terms" 
          className="text-sm font-normal cursor-pointer"
          onClick={() => form.setValue('terms', !form.watch('terms'))}
        >
          {t('I agree to the')} <Link to="/terms" className="text-primary hover:underline">{t('Terms of Service')}</Link> {t('and')} <Link to="/privacy" className="text-primary hover:underline">{t('Privacy Policy')}</Link>
        </Label>
      </div>
      {form.formState.errors.terms && (
        <p className="text-sm text-destructive">{form.formState.errors.terms.message}</p>
      )}
      
      <Button
        type="submit"
        className="w-full bg-pink-gradient hover:opacity-90"
        disabled={isLoading}
      >
        {isLoading ? t('Loading...') : t('Create Anonymous Account')}
      </Button>
    </form>
  );
};
